import React, { useState, useCallback, useMemo } from "react";
import type {
    JSX,
    MouseEventHandler,
    ElementType,
    FocusEventHandler,
    PropsWithChildren,
    AriaAttributes,
} from "react";

import {
    Box,
    boxForwardRef,
    type BoxComponentWithDefault,
} from "../../../../helpers/common/Box/Box";
import { useDropdownStore } from "../../store/store";
import { createDisplayNameForDropdownElement } from "../../utils/getDisplayName";
import { createTestIdForDropdownElement } from "../../utils/getTestId";

import type { PropsWithTsId } from "../../../../types/react";

export const DROPDOWN_MENU_ITEM_TEST_ID =
    createTestIdForDropdownElement("menu-item");

export const DROPDOWN_ITEM_HEIGHT_MEASURE_TARGET_ATRIBUTE =
    "data-height-measure-target";
export const DROPDOWN_ITEM_HEIGHT_MEASURE_TARGET_ID = "dropdown-item";

export interface IDropdownItem
    extends PropsWithChildren<Partial<PropsWithTsId>> {
    isSelected?: boolean;
    /**
     * Если false, то элемент меню не получит атрибутов навигации и станет нефокусируемым.
     * Это нужно, если внутрь Item передаём другой фокусируемый элемент
     */
    withNavigationAttrs?: boolean;
}

export const DropdownItem = boxForwardRef<
    BoxComponentWithDefault<"button", IDropdownItem>
>(
    (
        {
            as = "button",
            isSelected = false,
            withNavigationAttrs = true,
            onFocus,
            onBlur,
            onClick,
            "data-tsid": tsid = DROPDOWN_MENU_ITEM_TEST_ID,
            ...props
        },
        ref,
    ): JSX.Element => {
        const [isActive, setIsActive] = useState(false);

        const {
            options: {
                withSelect,
                closeMenuAfterBlur,
                closeMenuAfterSelect,
                focusToButtonAfterSelect,
            },
            focusToMenuButton,
            preventBlurRef,
            closeMenu,
            isOurElement,
        } = useDropdownStore();

        const navigationAttrs = useMemo(() => {
            if (!withNavigationAttrs) {
                return null;
            }

            return {
                tabIndex: isActive ? 0 : -1,
                "data-is-active": isActive,
            };
        }, [isActive, withNavigationAttrs]);

        const ariaProps: AriaAttributes = useMemo(() => {
            return {
                role: withSelect ? "menuitemradio" : "menuitem",
                "aria-checked": withSelect ? isSelected : undefined,
            };
        }, [withSelect, isSelected]);

        const focusHandler: FocusEventHandler = useCallback(
            (e) => {
                setIsActive(true);
                onFocus?.(e);
            },
            [onFocus],
        );

        const blurHandler: FocusEventHandler = useCallback(
            (e) => {
                /**
                 * Если фокус ни на какой элемент не переместился, то игнорируем.
                 * Проблема заключается в том, что при клике на интерактивный элемент с disabled блокируются всплытие любых событий клика
                 * (https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#enabling-and-disabling-form-controls:-the-disabled-attribute).
                 * Из-за этого флаг preventBlurRef в Core.tsx устанавливается некорректно.
                 */
                if (!e.relatedTarget) {
                    return;
                }
                const isOurElementResult = isOurElement(e.relatedTarget);
                /** Если следующий активный элемент не входит в наше меню, то оставляем tabIndex="0" (isActive = true) */
                if (isOurElementResult) {
                    setIsActive(false);
                }
                if (preventBlurRef.current) {
                    return;
                }
                if (closeMenuAfterBlur && !isOurElementResult) {
                    closeMenu();
                }
                onBlur?.(e);
            },
            [
                isOurElement,
                preventBlurRef,
                closeMenuAfterBlur,
                onBlur,
                closeMenu,
            ],
        );

        const clickHandler: MouseEventHandler = useCallback(
            (e) => {
                if (closeMenuAfterSelect) {
                    closeMenu();
                }
                if (focusToButtonAfterSelect) {
                    focusToMenuButton();
                }
                onClick?.(e);
            },
            [
                closeMenuAfterSelect,
                focusToButtonAfterSelect,
                onClick,
                closeMenu,
                focusToMenuButton,
            ],
        );

        const componentProps = {
            as: as as ElementType,
            ...ariaProps,
            ...props,
            ...navigationAttrs,
            onFocus: focusHandler,
            onBlur: blurHandler,
            onClick: clickHandler,
            [DROPDOWN_ITEM_HEIGHT_MEASURE_TARGET_ATRIBUTE]:
                DROPDOWN_ITEM_HEIGHT_MEASURE_TARGET_ID,
            ref,
        };

        return <Box data-tsid={tsid} {...componentProps} />;
    },
);

DropdownItem.displayName = createDisplayNameForDropdownElement("Item");

export default DropdownItem;
